
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import LoadingSpinner from '../../components/LoadingSpinner';

const FranchiseUserList = ({ user }) => {
    const [franchiseId, setFranchiseId] = useState(null);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Unique values for dropdowns
    const [cities, setCities] = useState([]);
    const [states, setStates] = useState([]);

    useEffect(() => {
        const fetchFranchiseUsers = async () => {
            try {
                if (user?.uid) {
                    // Fetch franchiseId from users collection
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (!userDoc.exists()) {
                        console.error("User document not found!");
                        setLoading(false);
                        return;
                    }

                    const userData = userDoc.data();
                    const fId = userData.franchiseId || user.uid;
                    setFranchiseId(fId);

                    // Fetch users referred by/registered under this franchise from main users collection
                    // This handles both new users (with franchiseId) and older/stranded users (with only referredBy)
                    const usersRef = collection(db, 'users');
                    const q = query(usersRef, where('referredBy', '==', fId));
                    const usersSnapshot = await getDocs(q);

                    const usersData = usersSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Extract unique cities and states for filters
                    const uniqueCities = [...new Set(usersData.map(u => u.city).filter(Boolean))];
                    const uniqueStates = [...new Set(usersData.map(u => u.state).filter(Boolean))];

                    setUsers(usersData);
                    setFilteredUsers(usersData);
                    setCities(uniqueCities.sort());
                    setStates(uniqueStates.sort());

                    // Now fetch detailed info (City, State, Expiry) in parallel
                    const enrichedUsers = await Promise.all(usersData.map(async (user) => {
                        let city = 'N/A';
                        let state = 'N/A';
                        let expiryDate = null;

                        try {
                            // 1. Fetch Profile Details (City/State)
                            if (user.userType === 'employer') {
                                // For employers, fetch from 'companies' collection
                                const companyDoc = await getDoc(doc(db, 'companies', user.id));
                                if (companyDoc.exists()) {
                                    const companyData = companyDoc.data();
                                    // check various possible location fields
                                    city = companyData.city || companyData.district || companyData.location?.split(',')[0] || 'N/A';
                                    state = companyData.state || 'N/A';
                                }
                            } else {
                                // For seekers, fetch from 'seekerProfiles' collection (default)
                                const seekerDoc = await getDoc(doc(db, 'seekerProfiles', user.id));
                                if (seekerDoc.exists()) {
                                    const seekerData = seekerDoc.data();
                                    // Personal info usually contains address
                                    if (seekerData.personalInfo) {
                                        city = seekerData.personalInfo.city || seekerData.personalInfo.district || 'N/A';
                                        state = seekerData.personalInfo.state || 'N/A';
                                    }
                                }
                            }

                            // 2. Fetch Subscription Details (Expiry Date)
                            const subQuery = query(
                                collection(db, 'subscriptions'),
                                where('userId', '==', user.id),
                                where('status', '==', 'active')
                            );
                            const subSnapshot = await getDocs(subQuery);

                            if (!subSnapshot.empty) {
                                // Get the latest active subscription
                                // Sorting might be needed if multiple active (shouldn't be, but good practice)
                                const subDoc = subSnapshot.docs[0].data();
                                if (subDoc.endDate) {
                                    // Convert Timestamp to readable date
                                    const date = subDoc.endDate.toDate();
                                    expiryDate = date.toLocaleDateString(); // e.g., "12/31/2024"
                                }
                            }

                        } catch (innerErr) {
                            console.error(`Error fetching details for user ${user.id}:`, innerErr);
                        }

                        return {
                            ...user,
                            city: city !== 'N/A' ? city : user.city, // Fallback to user doc if profile missing
                            state: state !== 'N/A' ? state : user.state,
                            expiryDate
                        };
                    }));

                    setUsers(enrichedUsers);
                    setFilteredUsers(enrichedUsers);

                    // Re-extract unique values from enriched data
                    const enrichedCities = [...new Set(enrichedUsers.map(u => u.city).filter(c => c && c !== 'N/A'))];
                    const enrichedStates = [...new Set(enrichedUsers.map(u => u.state).filter(s => s && s !== 'N/A'))];
                    setCities(enrichedCities.sort());
                    setStates(enrichedStates.sort());

                }
            } catch (err) {
                console.error("Error fetching franchise users:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchFranchiseUsers();
    }, [user]);

    // Apply filters whenever filter states change
    useEffect(() => {
        let filtered = [...users];

        // Search filter (business name)
        if (searchTerm) {
            filtered = filtered.filter(u =>
                u.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // City filter
        if (selectedCity) {
            filtered = filtered.filter(u => u.city === selectedCity);
        }

        // State filter
        if (selectedState) {
            filtered = filtered.filter(u => u.state === selectedState);
        }

        // Date range filter
        if (startDate || endDate) {
            filtered = filtered.filter(u => {
                if (!u.expiryDate) return false;

                const expiry = new Date(u.expiryDate);
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;

                if (start && end) {
                    return expiry >= start && expiry <= end;
                } else if (start) {
                    return expiry >= start;
                } else if (end) {
                    return expiry <= end;
                }
                return true;
            });
        }

        setFilteredUsers(filtered);
    }, [searchTerm, selectedCity, selectedState, startDate, endDate, users]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedCity('');
        setSelectedState('');
        setStartDate('');
        setEndDate('');
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Franchise Users</h1>
                <p className="text-gray-600">Manage and view all users registered under your franchise</p>
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Filters</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Search Box */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search Business</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by business name..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                    </div>

                    {/* City Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                            <option value="">All Cities</option>
                            {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>

                    {/* State Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                            <option value="">All States</option>
                            {states.map(state => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                    </div>

                    {/* Reset Button (aligned with inputs) */}
                    <div className="flex items-end">
                        <button
                            onClick={handleReset}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>

                {/* Date Range Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                    </div>
                </div>
            </div>

            {/* Results Summary */}
            <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold text-gray-900">{filteredUsers.length}</span> of <span className="font-semibold text-gray-900">{users.length}</span> users
                </p>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Name / Business
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    City
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    State
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Expiry Date
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {user.businessName || `${user.firstName || ''} ${user.lastName || ''}`}
                                            </div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.userType === 'employer' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                                                {user.userType === 'employer' ? 'Employer' : 'Job Seeker'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600">{user.city || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600">{user.state || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600">
                                                {user.expiryDate ? (
                                                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${new Date(user.expiryDate) < new Date()
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-green-100 text-green-700'
                                                        }`}>
                                                        {user.expiryDate}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                        {users.length === 0 ? (
                                            <div>
                                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                                <p className="font-medium">No users found</p>
                                                <p className="text-sm mt-1">No users are registered under your franchise yet.</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <p className="font-medium">No results found</p>
                                                <p className="text-sm mt-1">Try adjusting your filters to find what you're looking for.</p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FranchiseUserList;
